import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import FormView from '../../common/FormView';
import Loading from '../../../commonComponents/Loading';
import RefereeQuestion, { RefereeQuestionTypes } from '../../../models/be/RefereeQuestion';
import RefereeQuestionField from './RefereeQuestionField';
import { ChildReference } from '../../../models/be/Reference';
import { getReferenceFromRefereeToken, postRefereeAnswers, getCandidateFromRefereeToken } from '../../../core/api';
import { hasValue } from '../../../lib/utils';
import { RefereeAnswer } from '../../../models/be/RefereeAnswer';
import PrimaryName from '../../../models/be/PrimaryName';
import RefereePageLayout from '../../layouts/RefereePageLayout';
import MessageBox from '../../common/MessageBox';
import Document from '../../../models/be/db/Document';
import RequesterNotesForReferee from '../../common/RequesterNotesForReferee';
import ReferenceReplacementDocument from '../../common/ReferenceReplacementDocument';
import './RefereeQuestions.less';
import { getRefereeTokenFromQueryOrLocalStorage } from '../../../core/domain';
import { statuses } from '../../../lib/ApiBase';
import RefereeExpiryPage from '../../common/RefereeExpiryPage';
import { SaveAndFinishLater } from '../refereeSaved/RefereeSaved';
import { useAsyncAction } from '../../../commonComponents/Hooks';

const RefereeQuestions = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [loading, errorMessage, performAsyncAction] = useAsyncAction(true);
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>();
	const [refereeToken, setRefereeToken] = React.useState<string>();
	const [candidatePrimaryName, setCandidatePrimaryName] = React.useState<PrimaryName>();
	const [childReference, setChildReference] = React.useState<ChildReference>();
	const [answers, setAnswers] = React.useState<RefereeAnswer[]>();
	const [answerErrorMessages, setAnswerErrorMessages] = React.useState<string[]>();
	const [showExpiryPage, setShowExpiryPage] = React.useState(false);
	const [showRefereeDocumentUpload, setShowRefereeDocumentUpload] = React.useState(false);

	React.useEffect(() => {
		// TODO: Once all pages are updated to new css, should update css for body and remove following line.
		// Add following line to reuse the css.
		document.body.classList.add('dataCapturePage');

		performAsyncAction(async () => {
			const [newRefereeToken, tokenIsValid] = await getRefereeTokenFromQueryOrLocalStorage(location);
			if (tokenIsValid) {
				try {
					const [newReference, newCandidatePrimaryName] = await Promise.all([
						getReferenceFromRefereeToken(newRefereeToken),
						getCandidateFromRefereeToken(newRefereeToken)
					]);
					const newAnswers =
						newReference?.childReference.refereeAnswers ??
						newReference?.childReference.refereeQuestions.map((question) => {
							if (question.type === RefereeQuestionTypes.Boolean) {
								return false;
							}
							if (question.type === RefereeQuestionTypes.RadioList) {
								return question.options[0].value; // Default to first radio to avoid validation issue.
							}
							return null as RefereeAnswer;
						});

					setCandidatePrimaryName(newCandidatePrimaryName);
					setChildReference(newReference.childReference);
					setAnswers(newAnswers);
					setAnswerErrorMessages(newAnswers.map(() => null));
				} catch (e) {
					if (e.status === statuses.unauthorized) {
						setShowExpiryPage(true);
					} else {
						throw e;
					}
				}
			} else {
				setShowExpiryPage(true);
			}
			setRefereeToken(newRefereeToken);
		});

		return () => {
			document.body.classList.remove('dataCapturePage');
		};
	}, []);

	const handleAnswerChange = (index: number) => (_name: string, value: RefereeAnswer, answerErrorMessage: string) => {
		const newAnswers = [...answers];
		newAnswers[index] = value;
		setAnswers(newAnswers);
		const newAnswerErrorMessages = [...answerErrorMessages];
		newAnswerErrorMessages[index] = answerErrorMessage;
		setAnswerErrorMessages(newAnswerErrorMessages);
	};

	const handleCloseFormErrorMessage = () => {
		setFormErrorMessage(null);
	};

	const handleBackButtonClick = async () => {
		navigate('/referee/validation');
	};

	const handleReviewButtonClick = async () => {
		const isValid = answerErrorMessages.filter((e) => e !== null && e !== undefined).length === 0;
		if (!isValid) {
			setFormErrorMessage('Please ensure all mandatory questions are correctly completed.');
			return;
		}
		await performAsyncAction(async () => {
			await saveData();
			navigate('/referee/review');
		});
	};

	const saveData = async () => {
		await performAsyncAction(async () => {
			await postRefereeAnswers(refereeToken, answers);
		});
	};

	const handleUploadDocumentClick = async () => {
		setShowRefereeDocumentUpload(true);
		return false;
	};

	const hideReferenceReplacementDocument = async (document: Document) => {
		setShowRefereeDocumentUpload(false);
		setChildReference({ ...childReference, referenceReplacementDocument: document });
	};

	const handleNavigateToPage = async (pageSlug: string) => {
		await saveData();
		navigate(`/referee/${pageSlug}`);
	};

	if (hasValue(errorMessage)) return <MessageBox error={errorMessage} />;

	if (showExpiryPage) {
		return <RefereeExpiryPage expiredRefereeToken={refereeToken} />;
	}

	if (loading) return <Loading />;

	// This has to be after showExpiryPage and loading.
	if (location.search.indexOf('refereeToken') !== -1) {
		navigate(location.pathname, { replace: true });
		return null;
	}

	return (
		<RefereePageLayout
			navigateToPage={handleNavigateToPage}
			className="refereeQuestionsPage"
			title="Questions"
			currentFlowProgressBarName="Questions"
		>
			<MessageBox error={formErrorMessage} onClose={handleCloseFormErrorMessage} />
			{childReference && showRefereeDocumentUpload ? (
				<ReferenceReplacementDocument
					refereeToken={refereeToken}
					reference={childReference}
					onBackButtonClick={hideReferenceReplacementDocument}
				/>
			) : (
				<>
					<p className="pageIntroduction">
						Please answer the questions about {candidatePrimaryName?.firstName} {candidatePrimaryName?.lastName} to the
						best of your ability as they will be shared with a potential employer. If the organisation already provides
						a document to serve as a reference, you can {/* TODO Change to button */}
						<a onClick={handleUploadDocumentClick}>upload it here</a>.
					</p>
					<RequesterNotesForReferee
						notes={childReference?.notesToReferee}
						consentToNotes={childReference?.refereeNotesAccept}
					/>
					<FormView
						className="k-form"
						submitButtonText="Review"
						cancelButtonText="Back"
						onFormSubmit={handleReviewButtonClick}
						onCancelButtonClick={handleBackButtonClick}
						centreButtons={false}
					>
						<fieldset className="fieldset dataCaptureSection">
							<div>
								{childReference?.refereeQuestions.map((x: RefereeQuestion, i: number) => (
									<QuestionFieldContainer>
										<RefereeQuestionField
											key={x.text}
											questionNumber={(i + 1).toString()}
											name={`refereeAnswers[${i}]`}
											refereeQuestion={x}
											refereeAnswer={answers[i]}
											onAnswerChange={handleAnswerChange(i)}
										/>
									</QuestionFieldContainer>
								))}
							</div>
						</fieldset>
					</FormView>
					<SaveAndFinishLater refereeToken={refereeToken} currentPagePath={location.pathname} onSave={saveData} />
				</>
			)}
		</RefereePageLayout>
	);
};

export default RefereeQuestions;

const QuestionFieldContainer = styled.div`
	label {
		white-space: pre-wrap;
	}
`;
